import { InputSummary, InputSummaryItem } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';
import { UnitValueValidation } from '../../../helper/validation/validation-sub-modules/unit-value.validation';

export interface EmissionRefInterface {
  name: string;
  translationId: string;
  powerType: string;
  isCustom: boolean;
  databaseEmissionCollection: string;
  databaseEmissionReference: string;
  customEmission: Emission;
}

export class EmissionRef {
  name: string;
  translationId: string;
  powerType: string | null;
  isCustom: boolean;
  databaseEmissionCollection: string;
  databaseEmissionReference: string;
  customEmission: Emission;

  constructor(param: EmissionRefInterface = {
    name: '',
    translationId: null,
    powerType: '',
    isCustom: false,
    databaseEmissionCollection: null,
    databaseEmissionReference: null,
    customEmission: new Emission()
  }) {
    this.name = param.name;
    this.translationId = param.translationId;
    this.powerType = param.powerType;
    this.isCustom = param.isCustom;
    this.databaseEmissionCollection = param.databaseEmissionCollection;
    this.databaseEmissionReference = param.databaseEmissionReference;
    this.customEmission = new Emission(param.customEmission);
    if (param.databaseEmissionReference === '') {
      this.databaseEmissionReference = null;
    }
    if (param.databaseEmissionCollection === '') {
      this.databaseEmissionCollection = null;
    }
  }

  static deepCopy(emissionRef: EmissionRef) {
    let copy;
    if (emissionRef) {
      copy = new EmissionRef(emissionRef);
      return copy;
    }
    throw new Error('Emission ref copy failed');
  }

  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs = this.getSummaryInputs(path);
    return inputSummary;
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    if (this.isCustom) {
      inputs.push(...this.customEmission.getSummaryInputs([...path, 'customEmission']));
    } else {
      inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.name, this.getNameTranslation(), [...path, 'name']);
    }
    return inputs;
  }

  getNameTranslation() {
    return this.translationId ? this.translationId : this.name;
  }
}

export interface EmissionInterface {
  name: string;
  powerType: string;
  co2EmissionSolid: UnitValue;
  ch4EmissionSolid: UnitValue;
  n2oEmissionSolid: UnitValue;
  co2EmissionLiquid: UnitValue;
  ch4EmissionLiquid: UnitValue;
  n2oEmissionLiquid: UnitValue;
  co2EmissionGas: UnitValue;
  ch4EmissionGas: UnitValue;
  n2oEmissionGas: UnitValue;
  co2EmissionBaseload: UnitValue;
  ch4EmissionBaseload: UnitValue;
  n2oEmissionBaseload: UnitValue;
}

export class Emission {
  public name: string;
  public powerType: string;
  public co2EmissionSolid: UnitValue;
  public ch4EmissionSolid: UnitValue;
  public n2oEmissionSolid: UnitValue;
  public co2EmissionLiquid: UnitValue;
  public ch4EmissionLiquid: UnitValue;
  public n2oEmissionLiquid: UnitValue;
  public co2EmissionGas: UnitValue;
  public ch4EmissionGas: UnitValue;
  public n2oEmissionGas: UnitValue;
  public co2EmissionBaseload: UnitValue;
  public ch4EmissionBaseload: UnitValue;
  public n2oEmissionBaseload: UnitValue;

  constructor(param: EmissionInterface = {
    name: '',
    powerType: '',
    co2EmissionSolid: new UnitValue(),
    ch4EmissionSolid: new UnitValue(),
    n2oEmissionSolid: new UnitValue(),
    co2EmissionLiquid: new UnitValue(),
    ch4EmissionLiquid: new UnitValue(),
    n2oEmissionLiquid: new UnitValue(),
    co2EmissionGas: new UnitValue(),
    ch4EmissionGas: new UnitValue(),
    n2oEmissionGas: new UnitValue(),
    co2EmissionBaseload: new UnitValue(),
    ch4EmissionBaseload: new UnitValue(),
    n2oEmissionBaseload: new UnitValue(),
  }) {
    this.name = param.name;
    this.powerType = param.powerType;
    this.co2EmissionSolid = new UnitValue(param.co2EmissionSolid);
    this.ch4EmissionSolid = new UnitValue(param.ch4EmissionSolid);
    this.n2oEmissionSolid = new UnitValue(param.n2oEmissionSolid);
    this.co2EmissionLiquid = new UnitValue(param.co2EmissionLiquid);
    this.ch4EmissionLiquid = new UnitValue(param.ch4EmissionLiquid);
    this.n2oEmissionLiquid = new UnitValue(param.n2oEmissionLiquid);
    this.co2EmissionGas = new UnitValue(param.co2EmissionGas);
    this.ch4EmissionGas = new UnitValue(param.ch4EmissionGas);
    this.n2oEmissionGas = new UnitValue(param.n2oEmissionGas);
    this.co2EmissionBaseload = new UnitValue(param.co2EmissionBaseload);
    this.ch4EmissionBaseload = new UnitValue(param.ch4EmissionBaseload);
    this.n2oEmissionBaseload = new UnitValue(param.n2oEmissionBaseload);
  }

  static deepCopy(emission: Emission) {
    let copy;
    if (emission) {
      copy = new Emission(emission);
      return copy;
    }
    throw new Error('Emission copy failed');
  }

  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.co2EmissionSolid, this.co2EmissionSolid, [...path, 'co2EmissionSolid']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.ch4EmissionSolid, this.ch4EmissionSolid, [...path, 'ch4EmissionSolid']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.n2oEmissionSolid, this.n2oEmissionSolid, [...path, 'n2oEmissionSolid']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.co2EmissionLiquid, this.co2EmissionLiquid, [...path, 'co2EmissionLiquid']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.ch4EmissionLiquid, this.ch4EmissionLiquid, [...path, 'ch4EmissionLiquid']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.n2oEmissionLiquid, this.n2oEmissionLiquid, [...path, 'n2oEmissionLiquid']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.co2EmissionGas, this.co2EmissionGas, [...path, 'co2EmissionGas']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.ch4EmissionGas, this.ch4EmissionGas, [...path, 'ch4EmissionGas']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.n2oEmissionGas, this.n2oEmissionGas, [...path, 'n2oEmissionGas']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.co2EmissionBaseload, this.co2EmissionBaseload, [...path, 'co2EmissionBaseload']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.ch4EmissionBaseload, this.ch4EmissionBaseload, [...path, 'ch4EmissionBaseload']);
    inputs = InputSummaryUtils.performPush(inputs, EmissionPropName.n2oEmissionBaseload, this.n2oEmissionBaseload, [...path, 'n2oEmissionBaseload']);
    return inputs;
  }

  /**
   * returns s list of sensitivity inputs formatted list of valid properties
   * @param path - the path up to this input
   * @param prefix - the prefix for the name, used to create the path style name
   * @param nameArray
   */
  getSensInputs(prefix: string, nameArray: string[], path: string[]) {
    const inputs: { id: string, name: string[], path: string[], value: number, unit: string }[] = [];
    inputs.push(...this.getSolidInputs(prefix, nameArray, path));
    inputs.push(...this.getLiquidInputs(prefix, nameArray, path));
    inputs.push(...this.getGasInputs(prefix, nameArray, path));
    inputs.push(...this.getBaseloadInputs(prefix, nameArray, path));
    return inputs;
  }

  getGasInputs(prefix: string, nameArray: string[], path: string[]) {
    const inputs = [];
    if (this.co2EmissionGas && new UnitValueValidation(this.co2EmissionGas).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.co2EmissionGas,
        path.concat('co2EmissionGas'), this.co2EmissionGas.value, this.co2EmissionGas.unit));
    }
    if (this.ch4EmissionGas && new UnitValueValidation(this.ch4EmissionGas).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.ch4EmissionGas,
        path.concat('ch4EmissionGas'), this.ch4EmissionGas.value, this.ch4EmissionGas.unit));
    }
    if (this.n2oEmissionGas && new UnitValueValidation(this.n2oEmissionGas).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.n2oEmissionGas,
        path.concat('n2oEmissionGas'), this.n2oEmissionGas.value, this.n2oEmissionGas.unit));
    }
    return inputs;
  }

  getBaseloadInputs(prefix: string, nameArray: string[], path: string[]) {
    const inputs = [];
    if (this.co2EmissionBaseload && new UnitValueValidation(this.co2EmissionBaseload).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.co2EmissionBaseload,
        path.concat('co2EmissionBaseload'), this.co2EmissionBaseload.value, this.co2EmissionBaseload.unit));
    }
    if (this.ch4EmissionBaseload && new UnitValueValidation(this.ch4EmissionBaseload).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.ch4EmissionBaseload,
        path.concat('ch4EmissionBaseload'), this.ch4EmissionBaseload.value, this.ch4EmissionBaseload.unit));
    }
    if (this.n2oEmissionBaseload && new UnitValueValidation(this.n2oEmissionBaseload).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.n2oEmissionBaseload,
        path.concat('n2oEmissionBaseload'), this.n2oEmissionBaseload.value, this.n2oEmissionBaseload.unit));
    }
    return inputs;
  }

  private getSolidInputs(prefix: string, nameArray: string[], path: string[]) {
    const inputs = [];
    if (this.co2EmissionSolid && new UnitValueValidation(this.co2EmissionSolid).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.co2EmissionSolid,
        path.concat('co2EmissionSolid'), this.co2EmissionSolid.value, this.co2EmissionSolid.unit));
    }
    if (this.ch4EmissionSolid && new UnitValueValidation(this.ch4EmissionSolid).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.ch4EmissionSolid,
        path.concat('ch4EmissionSolid'), this.ch4EmissionSolid.value, this.ch4EmissionSolid.unit));
    }
    if (this.n2oEmissionSolid && new UnitValueValidation(this.n2oEmissionSolid).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.n2oEmissionSolid,
        path.concat('n2oEmissionSolid'), this.n2oEmissionSolid.value, this.n2oEmissionSolid.unit));
    }
    return inputs;
  }

  private getLiquidInputs(prefix: string, nameArray: string[], path: string[]) {
    const inputs = [];
    if (this.co2EmissionLiquid && new UnitValueValidation(this.co2EmissionLiquid).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.co2EmissionLiquid,
        path.concat('co2EmissionLiquid'), this.co2EmissionLiquid.value, this.co2EmissionLiquid.unit));
    }
    if (this.ch4EmissionLiquid && new UnitValueValidation(this.ch4EmissionLiquid).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.ch4EmissionLiquid,
        path.concat('ch4EmissionLiquid'), this.ch4EmissionLiquid.value, this.ch4EmissionLiquid.unit));
    }
    if (this.n2oEmissionLiquid && new UnitValueValidation(this.n2oEmissionLiquid).isValid()) {
      inputs.push(this.createSensInputMeta(prefix, nameArray, EmissionPropName.n2oEmissionLiquid,
        path.concat('n2oEmissionLiquid'), this.n2oEmissionLiquid.value, this.n2oEmissionLiquid.unit));
    }
    return inputs;
  }

  private createSensInputMeta(
    prefix: string,
    nameArray: string[],
    name: string,
    path: string[],
    value: number,
    unit: string
  ) {
    return { id: prefix + ', ' + name,  name: nameArray.push(name), path: path.concat('value'), value: value, unit: unit };
  }
}

export enum EmissionPropName {
  name = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.NAME',
  powerType = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.POWER_TYPE',
  isCustom = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.IS_CUSTOM',
  co2EmissionSolid = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.CO2_EMISSION_SOLID',
  ch4EmissionSolid = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.CH4_EMISSION_SOLID',
  n2oEmissionSolid = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.N2O_EMISSION_SOLID',
  co2EmissionLiquid = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.CO2_EMISSION_LIQUID',
  ch4EmissionLiquid = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.CH4_EMISSION_LIQUID',
  n2oEmissionLiquid = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.N2O_EMISSION_LIQUID',
  co2EmissionGas = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.CO2_EMISSION_GAS',
  ch4EmissionGas = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.CH4_EMISSION_GAS',
  n2oEmissionGas = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.N2O_EMISSION_GAS',
  co2EmissionBaseload = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.CO2_EMISSION_BASELOAD',
  ch4EmissionBaseload = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.CH4_EMISSION_BASELOAD',
  n2oEmissionBaseload = 'INPUT_SUMMARY.GHG_EMISSIONS.EMISSION.N2O_EMISSION_BASELOAD'
}
